import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ShoppingCart, 
  Package, 
  Truck, 
  CheckCircle, 
  Clock, 
  Search, 
  Filter, 
  Plus, 
  Eye, 
  Edit, 
  Trash2, 
  Check, 
  X, 
  ChevronDown, 
  ChevronRight, 
  X as CloseIcon,
  RotateCcw,
  Building2,
  User,
  Users,
  Calendar,
  DollarSign,
  FileText,
  Home,
  Phone,
  Mail,
  Minus,
  Tag,
  BarChart3,
  Receipt,
  Layers,
  ShoppingBag,
  Printer,
  ArrowLeft,
  ArrowRight,
  Loader2,
  ArrowLeft as BackIcon,
  Truck as TruckIcon,
  CreditCard,
  Calculator
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import Layout from '../components/Layout';
import toast from 'react-hot-toast';
import apiService from '../services/apiService';

const ReceivePurchases = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { formatCurrency, currencySymbol } = useSettings();
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [showBulkReceiveModal, setShowBulkReceiveModal] = useState(false);
  const [selectedPurchaseOrder, setSelectedPurchaseOrder] = useState(null);
  const [bulkReceiveLoading, setBulkReceiveLoading] = useState(false);
  const [approvedPurchases, setApprovedPurchases] = useState([]);
  const [supplierInvoiceNumber, setSupplierInvoiceNumber] = useState('');
  const [receivedQuantities, setReceivedQuantities] = useState({});
  const [additionalItems, setAdditionalItems] = useState([]);
  const [removedItems, setRemovedItems] = useState(new Set());
  
  // Receipt modal state
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  
  // View modal state
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewData, setViewData] = useState(null);
  
  // Product search states
  const [searchQuery, setSearchQuery] = useState('');
  const [products, setProducts] = useState([]);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [currentQuantity, setCurrentQuantity] = useState(0);
  const [suppliers, setSuppliers] = useState([]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Company settings state
  const [companySettings, setCompanySettings] = useState({
    company_name: 'NEXAPOS',
    company_address: '123 Business Street, Accra',
    company_phone: '+233 24 123 4567',
    tax_number: ''
  });

  // Fetch company settings
  const fetchCompanySettings = async () => {
    try {
      const response = await apiService.getSettings();
      setCompanySettings({
        company_name: response.data.company_name || 'NEXAPOS',
        company_address: response.data.company_address || '123 Business Street, Accra',
        company_phone: response.data.company_phone || '+233 24 123 4567',
        tax_number: response.data.tax_number || ''
      });
    } catch (err) {
      console.error('Error fetching company settings:', err);
    }
  };

  // Fetch received purchases for display
  const fetchReceivedPurchases = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        per_page: perPage,
        ...(searchTerm && { search: searchTerm }),
        ...(supplierFilter && { supplier_id: supplierFilter })
      });

      const response = await apiService.request(`/purchases/received?${params}`);
      setPurchases(response.data.data);
      setTotalPages(response.data.last_page);
      setTotalItems(response.data.total);
      setError(null);
    } catch (err) {
      setError('Failed to fetch received purchases');
      console.error('Error fetching received purchases:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch suppliers
  const fetchSuppliers = async () => {
    try {
      const response = await apiService.request('/suppliers');
      setSuppliers(response.data);
    } catch (err) {
      console.error('Error fetching suppliers:', err);
    }
  };

  // Fetch approved purchases for bulk receive modal dropdown
  const fetchApprovedPurchasesForModal = async () => {
    try {
      const response = await apiService.request('/purchases/approved');
      setApprovedPurchases(response.data.data || []);
    } catch (err) {
      console.error('Error fetching approved purchases for modal:', err);
    }
  };

  // Effects
  useEffect(() => {
    fetchReceivedPurchases();
  }, [currentPage, perPage, searchTerm, supplierFilter]);

  useEffect(() => {
    fetchSuppliers();
    fetchCompanySettings();
  }, []);

  // Click outside to close product dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showProductDropdown && !event.target.closest('.product-search-container')) {
        setShowProductDropdown(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProductDropdown]);


  // Handlers

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleSupplierFilter = (supplierId) => {
    setSupplierFilter(supplierId);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePerPageChange = (newPerPage) => {
    setPerPage(newPerPage);
    setCurrentPage(1);
  };

  const handleReceivePurchase = async (purchaseId) => {
    try {
      await apiService.request(`/purchases/${purchaseId}/receive`, {
        method: 'POST'
      });
      
      // Refresh the list
      fetchReceivedPurchases();
      
      // Show success message
      alert('Purchase received successfully! Stock has been updated.');
    } catch (err) {
      console.error('Error receiving purchase:', err);
      alert('Failed to receive purchase');
    }
  };

  // Bulk receive handlers
  const handleOpenBulkReceiveModal = () => {
    setSelectedPurchaseOrder(null);
    setShowBulkReceiveModal(true);
    fetchApprovedPurchasesForModal(); // Fetch approved purchases for dropdown
  };

  const handleCloseBulkReceiveModal = () => {
    setShowBulkReceiveModal(false);
    setSelectedPurchaseOrder(null);
    setSupplierInvoiceNumber('');
    setReceivedQuantities({});
    setAdditionalItems([]);
    setRemovedItems(new Set());
    setSearchQuery('');
    setProducts([]);
    setShowProductDropdown(false);
    setSelectedIndex(-1);
    setCurrentQuantity(0);
  };

  const handleQuantityChange = (itemId, quantity) => {
    setReceivedQuantities(prev => ({
      ...prev,
      [itemId]: Math.max(0, quantity)
    }));
  };

  const handleRemoveItem = (itemId) => {
    setRemovedItems(prev => new Set([...prev, itemId]));
    // Set received quantity to 0 for removed items
    setReceivedQuantities(prev => ({
      ...prev,
      [itemId]: 0
    }));
  };

  const handleRestoreItem = (itemId) => {
    setRemovedItems(prev => {
      const newSet = new Set(prev);
      newSet.delete(itemId);
      return newSet;
    });
    // Restore original quantity
    const originalItem = selectedPurchaseOrder?.items?.find(item => item.id === itemId);
    if (originalItem) {
      setReceivedQuantities(prev => ({
        ...prev,
        [itemId]: originalItem.quantity
      }));
    }
  };

  // Receipt modal handlers
  const handleOpenReceiptModal = (orderData) => {
    setReceiptData(orderData);
    setShowReceiptModal(true);
  };

  const handleCloseReceiptModal = () => {
    setShowReceiptModal(false);
    setReceiptData(null);
  };

  const handlePrintReceipt = () => {
    // Create a temporary div with the receipt content
    const printContent = document.createElement('div');
    printContent.innerHTML = `
      <div style="font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; max-width: 800px; margin: 0 auto; padding: 20px; background: white;">
        <!-- Header Section -->
        <div style="text-align: center; margin-bottom: 30px; border-bottom: 3px solid #2563eb; padding-bottom: 20px;">
          <h1 style="font-size: 32px; font-weight: bold; color: #1f2937; margin: 0 0 8px 0; letter-spacing: 2px;">${companySettings.company_name}</h1>
          <p style="font-size: 16px; color: #6b7280; margin: 0 0 15px 0; font-weight: 500;">Purchase Order Received Receipt</p>
          <div style="display: flex; justify-content: space-between; align-items: center; font-size: 12px; color: #6b7280;">
            <span><strong>PO Number:</strong> ${receiptData.purchase_order_number}</span>
            <span><strong>Date:</strong> ${new Date(receiptData.created_at).toLocaleDateString('en-US')}</span>
            <span><strong>Time:</strong> ${new Date(receiptData.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>
            <span><strong>Processed by:</strong> ${user?.name || 'Agent'}</span>
          </div>
        </div>

        <!-- Supplier and Company Section -->
        <div style="margin-bottom: 30px; display: flex; gap: 30px;">
          <!-- Supplier Section (Left) -->
          <div style="flex: 1;">
            <h3 style="font-size: 18px; font-weight: bold; color: #1f2937; margin: 0 0 15px 0; border-left: 4px solid #2563eb; padding-left: 12px;">Supplier Details</h3>
            <div style="background: #f8fafc; padding: 20px; border-radius: 8px; border: 1px solid #e2e8f0;">
              <div style="font-size: 18px; font-weight: bold; color: #1f2937; margin-bottom: 8px;">${receiptData.supplier?.name || 'Unknown Supplier'}</div>
              <div style="font-size: 14px; color: #6b7280; margin-bottom: 4px;">📞 ${receiptData.supplier?.phone || 'N/A'}</div>
              <div style="font-size: 14px; color: #6b7280; margin-bottom: 4px;">✉️ ${receiptData.supplier?.email || 'N/A'}</div>
              <div style="font-size: 14px; color: #6b7280;">📍 ${receiptData.supplier?.address || 'N/A'}</div>
            </div>
          </div>
          
          <!-- Company Section (Right) -->
          <div style="flex: 1;">
            <h3 style="font-size: 18px; font-weight: bold; color: #1f2937; margin: 0 0 15px 0; border-left: 4px solid #2563eb; padding-left: 12px;">Company Details</h3>
            <div style="background: #f8fafc; padding: 20px; border-radius: 8px; border: 1px solid #e2e8f0;">
              <div style="font-size: 18px; font-weight: bold; color: #1f2937; margin-bottom: 8px;">${companySettings.company_name}</div>
              <div style="font-size: 14px; color: #6b7280; margin-bottom: 4px;">📍 ${companySettings.company_address}</div>
              <div style="font-size: 14px; color: #6b7280;">📞 ${companySettings.company_phone}</div>
              ${companySettings.tax_number ? `<div style="font-size: 14px; color: #6b7280;">Tax ID: ${companySettings.tax_number}</div>` : ''}
            </div>
          </div>
        </div>

        <!-- Order Information Table -->
        <div style="margin-bottom: 30px;">
          <h3 style="font-size: 18px; font-weight: bold; color: #1f2937; margin: 0 0 15px 0; border-left: 4px solid #2563eb; padding-left: 12px;">Order Information</h3>
          <table style="width: 100%; border-collapse: collapse; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden;">
            <thead>
              <tr style="background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);">
                <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: left; font-weight: bold; color: #374151; font-size: 14px;">Received Date</th>
                <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: center; font-weight: bold; color: #374151; font-size: 14px;">Mode of Purchase</th>
                <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: center; font-weight: bold; color: #374151; font-size: 14px;">Status</th>
                <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: right; font-weight: bold; color: #374151; font-size: 14px;">Agent</th>
              </tr>
            </thead>
            <tbody>
              <tr style="background: #f8fafc;">
                <td style="border: 1px solid #e2e8f0; padding: 12px; font-weight: 500; color: #1f2937; font-size: 14px;">${new Date().toLocaleDateString('en-US')}</td>
                <td style="border: 1px solid #e2e8f0; padding: 12px; text-align: center; font-weight: 500; color: #1f2937; font-size: 14px; text-transform: capitalize;">${receiptData.mode_of_purchase?.replace('_', ' ') || 'N/A'}</td>
                <td style="border: 1px solid #e2e8f0; padding: 12px; text-align: center; color: #059669; font-size: 14px; font-weight: bold;">Received</td>
                <td style="border: 1px solid #e2e8f0; padding: 12px; text-align: right; color: #374151; font-size: 14px;">${receiptData.agent || user?.name || 'System User'}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Order Items Table -->
        <div style="margin-bottom: 30px;">
          <h3 style="font-size: 18px; font-weight: bold; color: #1f2937; margin: 0 0 15px 0; border-left: 4px solid #2563eb; padding-left: 12px;">Order Items</h3>
          <table style="width: 100%; border-collapse: collapse; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden;">
            <thead>
              <tr style="background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);">
                <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: left; font-weight: bold; color: #374151; font-size: 14px;">Product</th>
                <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: center; font-weight: bold; color: #374151; font-size: 14px;">Quantity</th>
                <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: center; font-weight: bold; color: #374151; font-size: 14px;">UOM</th>
                <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: right; font-weight: bold; color: #374151; font-size: 14px;">Price</th>
                <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: right; font-weight: bold; color: #374151; font-size: 14px;">Amount</th>
                <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: center; font-weight: bold; color: #374151; font-size: 14px;">Status</th>
              </tr>
            </thead>
            <tbody>
              ${receiptData.items?.map(item => `
                <tr style="background: #f8fafc;">
                  <td style="border: 1px solid #e2e8f0; padding: 12px; font-weight: 500; color: #1f2937; font-size: 14px;">${item.product?.name || 'Unknown Product'}</td>
                  <td style="border: 1px solid #e2e8f0; padding: 12px; text-align: center; font-weight: bold; color: #1f2937; font-size: 14px;">${item.quantity}</td>
                  <td style="border: 1px solid #e2e8f0; padding: 12px; text-align: center; color: #374151; font-size: 14px;">${item.product?.unit_of_measure || 'pcs'}</td>
                  <td style="border: 1px solid #e2e8f0; padding: 12px; text-align: right; font-weight: bold; color: #1f2937; font-size: 14px;">${currencySymbol} ${(Number(item.cost_price) || 0).toFixed(2)}</td>
                  <td style="border: 1px solid #e2e8f0; padding: 12px; text-align: right; font-weight: bold; color: #1f2937; font-size: 14px;">${currencySymbol} ${(Number(item.subtotal) || 0).toFixed(2)}</td>
                  <td style="border: 1px solid #e2e8f0; padding: 12px; text-align: center; color: #059669; font-size: 14px; font-weight: bold;">Received</td>
                </tr>
              `).join('') || '<tr><td colspan="6" style="text-align: center; padding: 20px; color: #6b7280;">No items found</td></tr>'}
            </tbody>
          </table>
        </div>

        <!-- Order Summary -->
        <div style="background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%); padding: 25px; border-radius: 12px; border: 1px solid #e2e8f0;">
          <h3 style="font-size: 18px; font-weight: bold; color: #1f2937; margin: 0 0 20px 0; border-left: 4px solid #2563eb; padding-left: 12px;">Order Summary</h3>
          <div style="space-y: 12px;">
            <div style="display: flex; justify-content: space-between; align-items: center; padding: 15px 0; border-top: 2px solid #2563eb; margin-top: 15px;">
              <span style="font-size: 20px; font-weight: bold; color: #1f2937;">Total Amount:</span>
              <span style="font-size: 24px; font-weight: bold; color: #2563eb;">${currencySymbol} ${(Number(receiptData.total_cost) || 0).toFixed(2)}</span>
            </div>
          </div>
        </div>

        ${receiptData.remark ? `
        <!-- Notes -->
        <div style="background: #f8fafc; padding: 20px; border-radius: 8px; border: 1px solid #e2e8f0; margin: 20px 0;">
          <h3 style="font-size: 16px; font-weight: bold; color: #1f2937; margin: 0 0 10px 0;">Notes</h3>
          <p style="font-size: 14px; color: #6b7280; margin: 0;">${receiptData.remark}</p>
        </div>
        ` : ''}

        <!-- Footer -->
        <div style="text-align: center; margin-top: 30px; padding-top: 20px; border-top: 2px solid #e2e8f0; color: #6b7280; font-size: 12px;">
          <p style="margin: 0 0 8px 0;">Thank you for your business!</p>
          <p style="margin: 0 0 8px 0;">Keep this receipt for your records</p>
          <p style="margin: 0; font-weight: bold;">Powered By SherSoft Technologies - 0548104944</p>
        </div>
      </div>
    `;
    
    // Create hidden iframe for printing without opening new tab
    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.left = '-9999px';
    iframe.style.top = '-9999px';
    iframe.style.width = '210mm';
    iframe.style.height = '297mm';
    document.body.appendChild(iframe);
    
    const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
    iframeDoc.open();
    iframeDoc.write(`
      <html>
        <head>
          <title>Purchase Order Receipt - ${receiptData.purchase_order_number}</title>
          <style>
            @page {
              size: A4;
              margin: 20mm;
            }
            body { 
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
              margin: 0; 
              padding: 0;
              background: white;
              color: #333;
            }
            .receipt { 
              max-width: 800px; 
              margin: 0 auto; 
              padding: 20px;
              background: white;
            }
            @media print {
              body { margin: 0; padding: 0; }
              .receipt { border: none; }
            }
          </style>
        </head>
        <body>
          <div class="receipt">${printContent.innerHTML}</div>
        </body>
      </html>
      `);
    iframeDoc.close();
    
    // Wait for content to load, then print
    iframe.onload = () => {
      iframe.contentWindow.print();
      // Clean up iframe after printing
    setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);
    };
    
    // Show print notification
    toast.success('Purchase order receipt sent to printer!', {
      duration: 2000,
      icon: '🖨️',
      style: {
        background: '#10B981',
        color: '#fff',
        fontSize: '14px',
        padding: '12px',
        borderRadius: '8px',
      },
    });
  };

  const generatePDF = () => {
    // Create a temporary div with the receipt content
    const printContent = document.createElement('div');
    printContent.innerHTML = `
      <div style="font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; max-width: 800px; margin: 0 auto; padding: 20px; background: white;">
        <!-- Header Section -->
        <div style="text-align: center; margin-bottom: 30px; border-bottom: 3px solid #2563eb; padding-bottom: 20px;">
          <h1 style="font-size: 32px; font-weight: bold; color: #1f2937; margin: 0 0 8px 0; letter-spacing: 2px;">${companySettings.company_name}</h1>
          <p style="font-size: 16px; color: #6b7280; margin: 0 0 15px 0; font-weight: 500;">Purchase Order Received Receipt</p>
          <div style="display: flex; justify-content: space-between; align-items: center; font-size: 12px; color: #6b7280;">
            <span><strong>PO Number:</strong> ${receiptData.purchase_order_number}</span>
            <span><strong>Date:</strong> ${new Date(receiptData.created_at).toLocaleDateString('en-US')}</span>
            <span><strong>Time:</strong> ${new Date(receiptData.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>
            <span><strong>Processed by:</strong> ${user?.name || 'Agent'}</span>
          </div>
        </div>

        <!-- Supplier and Company Section -->
        <div style="margin-bottom: 30px; display: flex; gap: 30px;">
          <!-- Supplier Section (Left) -->
          <div style="flex: 1;">
            <h3 style="font-size: 18px; font-weight: bold; color: #1f2937; margin: 0 0 15px 0; border-left: 4px solid #2563eb; padding-left: 12px;">Supplier Details</h3>
            <div style="background: #f8fafc; padding: 20px; border-radius: 8px; border: 1px solid #e2e8f0;">
              <div style="font-size: 18px; font-weight: bold; color: #1f2937; margin-bottom: 8px;">${receiptData.supplier?.name || 'Unknown Supplier'}</div>
              <div style="font-size: 14px; color: #6b7280; margin-bottom: 4px;">📞 ${receiptData.supplier?.phone || 'N/A'}</div>
              <div style="font-size: 14px; color: #6b7280; margin-bottom: 4px;">✉️ ${receiptData.supplier?.email || 'N/A'}</div>
              <div style="font-size: 14px; color: #6b7280;">📍 ${receiptData.supplier?.address || 'N/A'}</div>
            </div>
          </div>
          
          <!-- Company Section (Right) -->
          <div style="flex: 1;">
            <h3 style="font-size: 18px; font-weight: bold; color: #1f2937; margin: 0 0 15px 0; border-left: 4px solid #2563eb; padding-left: 12px;">Company Details</h3>
            <div style="background: #f8fafc; padding: 20px; border-radius: 8px; border: 1px solid #e2e8f0;">
              <div style="font-size: 18px; font-weight: bold; color: #1f2937; margin-bottom: 8px;">${companySettings.company_name}</div>
              <div style="font-size: 14px; color: #6b7280; margin-bottom: 4px;">📍 ${companySettings.company_address}</div>
              <div style="font-size: 14px; color: #6b7280;">📞 ${companySettings.company_phone}</div>
              ${companySettings.tax_number ? `<div style="font-size: 14px; color: #6b7280;">Tax ID: ${companySettings.tax_number}</div>` : ''}
            </div>
          </div>
        </div>

        <!-- Order Information Table -->
        <div style="margin-bottom: 30px;">
          <h3 style="font-size: 18px; font-weight: bold; color: #1f2937; margin: 0 0 15px 0; border-left: 4px solid #2563eb; padding-left: 12px;">Order Information</h3>
          <table style="width: 100%; border-collapse: collapse; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden;">
            <thead>
              <tr style="background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);">
                <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: left; font-weight: bold; color: #374151; font-size: 14px;">Received Date</th>
                <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: center; font-weight: bold; color: #374151; font-size: 14px;">Mode of Purchase</th>
                <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: center; font-weight: bold; color: #374151; font-size: 14px;">Status</th>
                <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: right; font-weight: bold; color: #374151; font-size: 14px;">Agent</th>
              </tr>
            </thead>
            <tbody>
              <tr style="background: #f8fafc;">
                <td style="border: 1px solid #e2e8f0; padding: 12px; font-weight: 500; color: #1f2937; font-size: 14px;">${new Date().toLocaleDateString('en-US')}</td>
                <td style="border: 1px solid #e2e8f0; padding: 12px; text-align: center; font-weight: 500; color: #1f2937; font-size: 14px; text-transform: capitalize;">${receiptData.mode_of_purchase?.replace('_', ' ') || 'N/A'}</td>
                <td style="border: 1px solid #e2e8f0; padding: 12px; text-align: center; color: #059669; font-size: 14px; font-weight: bold;">Received</td>
                <td style="border: 1px solid #e2e8f0; padding: 12px; text-align: right; color: #374151; font-size: 14px;">${receiptData.agent || user?.name || 'System User'}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Order Items Table -->
        <div style="margin-bottom: 30px;">
          <h3 style="font-size: 18px; font-weight: bold; color: #1f2937; margin: 0 0 15px 0; border-left: 4px solid #2563eb; padding-left: 12px;">Order Items</h3>
          <table style="width: 100%; border-collapse: collapse; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden;">
            <thead>
              <tr style="background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);">
                <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: left; font-weight: bold; color: #374151; font-size: 14px;">Product</th>
                <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: center; font-weight: bold; color: #374151; font-size: 14px;">Quantity</th>
                <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: center; font-weight: bold; color: #374151; font-size: 14px;">UOM</th>
                <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: right; font-weight: bold; color: #374151; font-size: 14px;">Price</th>
                <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: right; font-weight: bold; color: #374151; font-size: 14px;">Amount</th>
                <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: center; font-weight: bold; color: #374151; font-size: 14px;">Status</th>
              </tr>
            </thead>
            <tbody>
              ${receiptData.items?.map(item => `
                <tr style="background: #f8fafc;">
                  <td style="border: 1px solid #e2e8f0; padding: 12px; font-weight: 500; color: #1f2937; font-size: 14px;">${item.product?.name || 'Unknown Product'}</td>
                  <td style="border: 1px solid #e2e8f0; padding: 12px; text-align: center; font-weight: bold; color: #1f2937; font-size: 14px;">${item.quantity}</td>
                  <td style="border: 1px solid #e2e8f0; padding: 12px; text-align: center; color: #374151; font-size: 14px;">${item.product?.unit_of_measure || 'pcs'}</td>
                  <td style="border: 1px solid #e2e8f0; padding: 12px; text-align: right; font-weight: bold; color: #1f2937; font-size: 14px;">${currencySymbol} ${(Number(item.cost_price) || 0).toFixed(2)}</td>
                  <td style="border: 1px solid #e2e8f0; padding: 12px; text-align: right; font-weight: bold; color: #1f2937; font-size: 14px;">${currencySymbol} ${(Number(item.subtotal) || 0).toFixed(2)}</td>
                  <td style="border: 1px solid #e2e8f0; padding: 12px; text-align: center; color: #059669; font-size: 14px; font-weight: bold;">Received</td>
                </tr>
              `).join('') || '<tr><td colspan="6" style="text-align: center; padding: 20px; color: #6b7280;">No items found</td></tr>'}
            </tbody>
          </table>
        </div>

        <!-- Order Summary -->
        <div style="background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%); padding: 25px; border-radius: 12px; border: 1px solid #e2e8f0;">
          <h3 style="font-size: 18px; font-weight: bold; color: #1f2937; margin: 0 0 20px 0; border-left: 4px solid #2563eb; padding-left: 12px;">Order Summary</h3>
          <div style="space-y: 12px;">
            <div style="display: flex; justify-content: space-between; align-items: center; padding: 15px 0; border-top: 2px solid #2563eb; margin-top: 15px;">
              <span style="font-size: 20px; font-weight: bold; color: #1f2937;">Total Amount:</span>
              <span style="font-size: 24px; font-weight: bold; color: #2563eb;">${currencySymbol} ${(Number(receiptData.total_cost) || 0).toFixed(2)}</span>
            </div>
          </div>
        </div>

        ${receiptData.remark ? `
        <!-- Notes -->
        <div style="background: #f8fafc; padding: 20px; border-radius: 8px; border: 1px solid #e2e8f0; margin: 20px 0;">
          <h3 style="font-size: 16px; font-weight: bold; color: #1f2937; margin: 0 0 10px 0;">Notes</h3>
          <p style="font-size: 14px; color: #6b7280; margin: 0;">${receiptData.remark}</p>
        </div>
        ` : ''}

        <!-- Footer -->
        <div style="text-align: center; margin-top: 30px; padding-top: 20px; border-top: 2px solid #e2e8f0; color: #6b7280; font-size: 12px;">
          <p style="margin: 0 0 8px 0;">Thank you for your business!</p>
          <p style="margin: 0 0 8px 0;">Keep this receipt for your records</p>
          <p style="margin: 0; font-weight: bold;">Powered By SherSoft Technologies - 0548104944</p>
        </div>
      </div>
    `;
    
    // Create hidden iframe for PDF generation without opening new tab
    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.left = '-9999px';
    iframe.style.top = '-9999px';
    iframe.style.width = '210mm';
    iframe.style.height = '297mm';
    document.body.appendChild(iframe);
    
    const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
    iframeDoc.open();
    iframeDoc.write(`
      <html>
        <head>
          <title>Purchase Order Receipt - ${receiptData.purchase_order_number}</title>
          <style>
            @page {
              size: A4;
              margin: 20mm;
            }
            body { 
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
              margin: 0; 
              padding: 0;
              background: white;
              color: #333;
            }
            .receipt { 
              max-width: 800px; 
              margin: 0 auto; 
              padding: 20px;
              background: white;
            }
            @media print {
              body { margin: 0; padding: 0; }
              .receipt { border: none; }
            }
          </style>
        </head>
        <body>
          <div class="receipt">${printContent.innerHTML}</div>
        </body>
      </html>
      `);
    iframeDoc.close();
    
    // Wait for content to load, then print
    iframe.onload = () => {
      iframe.contentWindow.print();
      // Clean up iframe after printing
    setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);
    };
    
    // Show PDF notification
    toast.success('PDF ready for download. Use "Save as PDF" in print dialog.', {
      duration: 5000,
      icon: '📄',
      style: {
        background: '#3B82F6',
        color: '#fff',
        fontSize: '14px',
        padding: '12px',
        borderRadius: '8px',
      },
    });
  };

  const handleSendWhatsApp = () => {
    if (!receiptData?.supplier?.phone) {
      alert('Supplier phone number not available');
      return;
    }

    // Format phone number (remove any non-digit characters and add country code if needed)
    let phoneNumber = receiptData.supplier.phone.replace(/\D/g, '');
    if (phoneNumber.startsWith('0')) {
      phoneNumber = '233' + phoneNumber.substring(1); // Ghana country code
    } else if (!phoneNumber.startsWith('233')) {
      phoneNumber = '233' + phoneNumber;
    }

    // Create WhatsApp message
    const message = `Hello ${receiptData.supplier?.name || 'Supplier'},

Your purchase order ${receiptData.purchase_order_number} has been received and processed successfully.

Order Details:
- Order Date: ${new Date(receiptData.created_at).toLocaleDateString()}
- Total Amount: ${currencySymbol} ${Number(receiptData.total_cost).toFixed(2)}
- Status: Received

Please find the receipt attached for your records.

Thank you for your business!

Best regards,
NEXAPOS Team`;

    // Open WhatsApp
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  // View modal handlers
  const handleOpenViewModal = (purchaseData) => {
    setViewData(purchaseData);
    setShowViewModal(true);
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setViewData(null);
  };

  // Product search functions
  const searchProducts = async (query) => {
    if (!query.trim()) {
      setProducts([]);
      setShowProductDropdown(false);
      return;
    }

    try {
      const response = await apiService.request(`/products?search=${encodeURIComponent(query)}`);
      setProducts(response.data || []);
      setShowProductDropdown(true);
      setSelectedIndex(-1);
    } catch (err) {
      console.error('Error searching products:', err);
      setProducts([]);
      setShowProductDropdown(false);
    }
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    searchProducts(query);
  };

  const handleSearchFocus = () => {
    // Only show dropdown if there's a search query and products
    if (searchQuery.trim() && products.length > 0) {
      setShowProductDropdown(true);
    }
  };

  const handleKeyDown = (e) => {
    if (showProductDropdown && products.length > 0) {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => (prev < products.length - 1 ? prev + 1 : 0));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => (prev > 0 ? prev - 1 : products.length - 1));
          break;
        case 'Enter':
          e.preventDefault();
          if (selectedIndex >= 0 && selectedIndex < products.length) {
            handleProductSelect(products[selectedIndex]);
          }
          break;
        case 'Escape':
          setShowProductDropdown(false);
          setSelectedIndex(-1);
          break;
      }
    } else if (e.key === 'Enter' && searchQuery.trim()) {
      // If no dropdown is open but we have a search query, move to quantity field
      e.preventDefault();
      const quantityInput = document.getElementById('purchase-quantity-input');
      if (quantityInput) {
        quantityInput.focus();
      }
    }
  };

  const handleProductSelect = (product) => {
    setSearchQuery(product.name);
    setShowProductDropdown(false);
    setSelectedIndex(-1);
    setCurrentQuantity(product.current_stock || 0);
    
    // Auto-fill cost price if available
    const costPriceInput = document.getElementById('purchase-cost-price-input');
    if (costPriceInput) {
      costPriceInput.value = product.cost_price || product.selling_price || product.price || '';
    }
    
    // Move focus to quantity field
    setTimeout(() => {
      const quantityInput = document.getElementById('purchase-quantity-input');
      if (quantityInput) {
        quantityInput.focus();
        quantityInput.select(); // Select the default value for easy editing
      }
    }, 100);
  };

  const addToCart = () => {
    const quantityInput = document.getElementById('purchase-quantity-input');
    const uomSelect = document.getElementById('purchase-uom-select');
    const costPriceInput = document.getElementById('purchase-cost-price-input');

    if (!searchQuery.trim()) {
      toast.error('Please select a product');
      return;
    }

    const quantity = parseInt(quantityInput?.value) || 1;
    const uom = uomSelect?.value || 'pcs';
    const costPrice = parseFloat(costPriceInput?.value) || 0;

    if (costPrice <= 0) {
      toast.error('Please enter a valid cost price');
      return;
    }

    const newItem = {
      product_name: searchQuery,
      quantity: quantity,
      uom: uom,
      cost_price: costPrice,
      subtotal: quantity * costPrice
    };

    setAdditionalItems(prev => [newItem, ...prev]);
    
    // Clear inputs and reset form
    setSearchQuery('');
    if (quantityInput) quantityInput.value = '1';
    if (costPriceInput) costPriceInput.value = '';
    setCurrentQuantity(0);
    
    // Move focus back to search field for next item
    setTimeout(() => {
      const searchInput = document.querySelector('.product-search-container input');
      if (searchInput) {
        searchInput.focus();
      }
    }, 100);
    
    toast.success('Product added to additional items');
  };

  const handleReceiveOrder = async () => {
    if (!selectedPurchaseOrder) {
      toast.error('Please select a purchase order to receive');
      return;
    }

    // Check if the purchase is approved
    if (selectedPurchaseOrder.status !== 'approved') {
      toast.error(`Cannot receive purchase with status: ${selectedPurchaseOrder.status}. Only approved purchases can be received.`);
      return;
    }

    // Supplier invoice number is optional according to backend validation

    setBulkReceiveLoading(true);
    try {
      const requestData = {
        removed_items: Array.from(removedItems),
        additional_items: additionalItems
      };
      if (supplierInvoiceNumber.trim()) {
        requestData.supplier_invoice_number = supplierInvoiceNumber.trim();
      }

      const response = await apiService.request(`/purchases/${selectedPurchaseOrder.id}/receive`, {
        method: 'POST',
        body: JSON.stringify(requestData)
      });

      // Store the updated purchase data for the receipt
      const updatedPurchase = response.data;
      
      toast.success('Purchase order received successfully! Stock updated and supplier balance increased.');
      fetchReceivedPurchases();
      handleCloseBulkReceiveModal();
      
      // Open receipt modal with updated data
      handleOpenReceiptModal(updatedPurchase);
    } catch (err) {
      console.error('Error receiving purchase:', err);
      toast.error('Failed to receive purchase order');
    } finally {
      setBulkReceiveLoading(false);
    }
  };


  return (
    <Layout>
      <div className="space-y-6">
          {/* Hero Section */}
          <div className="relative overflow-hidden mt-20 bg-gradient-to-br from-green-50 via-white to-blue-50 rounded-3xl p-8 mb-8 shadow-2xl border border-green-200">
            {/* Background Pattern */}
            <div className="absolute inset-0 bg-gradient-to-br from-green-50 via-white to-blue-50 opacity-50"></div>
            <div className="absolute top-0 right-0 w-48 h-48 bg-gradient-to-br from-green-100 to-blue-100 rounded-full -translate-y-24 translate-x-24 opacity-30"></div>
            <div className="absolute bottom-0 left-0 w-36 h-36 bg-gradient-to-tr from-blue-100 to-green-100 rounded-full translate-y-18 -translate-x-18 opacity-30"></div>
            
            <div className="relative z-10">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-6">
                  <div className="relative">
                    <div className="w-16 h-16 bg-gradient-to-br from-green-500 via-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-2xl transform rotate-3 hover:rotate-0 transition-transform duration-300">
                      <TruckIcon size={32} className="text-white" />
                    </div>
                    <div className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center shadow-lg">
                      <Check size={12} className="text-white" />
                    </div>
                  </div>
                  <div>
                    <h1 className="text-4xl font-bold bg-gradient-to-r from-green-600 via-blue-600 to-indigo-600 bg-clip-text text-transparent mb-2">
                      Received Purchases
                    </h1>
                    <p className="text-gray-600 text-lg font-medium">
                      View and manage received purchase orders
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={handleOpenBulkReceiveModal}
                    className="px-6 py-3 bg-gradient-to-r from-green-500 to-blue-600 text-white rounded-xl hover:from-green-600 hover:to-blue-700 transition-all duration-200 flex items-center gap-2 font-semibold shadow-lg hover:shadow-xl transform hover:scale-105"
                  >
                    <TruckIcon size={20} />
                    Receive New Order
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {/* Total Received */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-600 mb-1">Total Received</p>
                  <p className="text-3xl font-bold text-green-900">
                    {loading ? <Loader2 className="animate-spin" size={24} /> : purchases.length || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg">
                  <TruckIcon className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            {/* Available to Return */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-600 mb-1">Available to Return</p>
                  <p className="text-3xl font-bold text-blue-900">
                    {loading ? <Loader2 className="animate-spin" size={24} /> : purchases.filter(p => p.status === 'received').length || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                  <RotateCcw className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            {/* Total Value */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-purple-600 mb-1">Total Value</p>
                  <p className="text-3xl font-bold text-purple-900">
                    {loading ? <Loader2 className="animate-spin" size={24} /> : formatCurrency(purchases.reduce((sum, p) => {
                      const cost = Number(p.total_cost) || 0;
                      return sum + (isNaN(cost) ? 0 : cost);
                    }, 0))}
                  </p>
                </div>
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                  <DollarSign className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            {/* Suppliers */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-orange-600 mb-1">Suppliers</p>
                  <p className="text-3xl font-bold text-orange-900">
                    {loading ? <Loader2 className="animate-spin" size={24} /> : new Set(purchases.map(p => p.supplier_id)).size || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
                  <Building2 className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>
          </div>

          {/* Filters and Search */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 mb-6">
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Search */}
              <div className="flex-1">
                <div className="relative">
                  <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by order number, supplier, or invoice..."
                    value={searchTerm}
                    onChange={handleSearch}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                  />
                </div>
              </div>

              {/* Supplier Filter */}
              <div className="lg:w-48">
                <select
                  value={supplierFilter}
                  onChange={(e) => handleSupplierFilter(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                >
                  <option value="">All Suppliers</option>
                  {suppliers.map((supplier) => (
                    <option key={supplier.id} value={supplier.id}>
                      {supplier.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Clear Filters */}
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSupplierFilter('');
                  setCurrentPage(1);
                }}
                className="px-6 py-3 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 rounded-xl hover:from-gray-200 hover:to-gray-300 transition-all duration-200 flex items-center gap-2 font-semibold shadow-sm hover:shadow-md transform hover:scale-105"
              >
                <X size={16} />
                Clear
              </button>
            </div>
          </div>

          {/* Approved Purchases Table */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
              <h3 className="text-lg font-semibold text-gray-900">Received Purchase Orders</h3>
              <p className="text-sm text-gray-500">View and manage received purchase orders</p>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="animate-spin text-red-500" size={32} />
                <span className="ml-3 text-gray-600">Loading approved purchases...</span>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <X className="mx-auto text-red-500" size={48} />
                  <p className="mt-2 text-gray-600">{error}</p>
                  <button
                    onClick={fetchReceivedPurchases}
                    className="mt-4 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                  >
                    Retry
                  </button>
                </div>
              </div>
            ) : purchases.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <CheckCircle className="mx-auto text-green-500" size={48} />
                  <p className="mt-2 text-gray-600 text-lg">No Received purchase Orders</p>
                  <p className="text-gray-400">No purchase orders received</p>
                </div>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order #</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Supplier</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Cost</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expected Delivery</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Approved Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {purchases.map((purchase) => (
                        <tr key={purchase.id} className="hover:bg-gradient-to-r hover:from-gray-50 hover:to-white transition-all duration-200">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-10 h-10 bg-gradient-to-br from-green-100 to-blue-100 rounded-full flex items-center justify-center">
                                <ShoppingCart size={16} className="text-green-600" />
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">{purchase.purchase_order_number}</div>
                                {purchase.supplier_invoice_number && (
                                  <div className="text-sm text-gray-500">Invoice: {purchase.supplier_invoice_number}</div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{purchase.supplier?.name || 'N/A'}</div>
                            <div className="text-sm text-gray-500">{purchase.contact_person || 'N/A'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{formatCurrency(purchase.total_cost)}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {purchase.expected_delivery_date 
                                ? new Date(purchase.expected_delivery_date).toLocaleDateString()
                                : 'N/A'
                              }
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {new Date(purchase.updated_at).toLocaleDateString()}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handleOpenViewModal(purchase)}
                                className="group/btn relative p-2 text-blue-600 bg-blue-100 hover:bg-blue-200 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md hover:scale-110"
                                title="View Details"
                              >
                                <Eye size={18} />
                                <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover/btn:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                                  View
                                </span>
                              </button>
                              <button
                                onClick={() => handleOpenReceiptModal(purchase)}
                                className="group/btn relative p-2 text-purple-600 bg-purple-100 hover:bg-purple-200 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md hover:scale-110"
                                title="Print Receipt"
                              >
                                <Printer size={18} />
                                <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover/btn:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                                  Receipt
                                </span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="bg-gradient-to-r from-gray-50 to-white px-6 py-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <span className="text-sm text-gray-700">
                        Showing {((currentPage - 1) * perPage) + 1} to {Math.min(currentPage * perPage, totalItems)} of {totalItems} results
                      </span>
                      <select
                        value={perPage}
                        onChange={(e) => handlePerPageChange(Number(e.target.value))}
                        className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      >
                        <option value={10}>10 per page</option>
                        <option value={15}>15 per page</option>
                        <option value={25}>25 per page</option>
                        <option value={50}>50 per page</option>
                      </select>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="px-4 py-2 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                      >
                        <ArrowLeft size={16} className="inline mr-1" />
                        Previous
                      </button>
                      <span className="px-4 py-2 text-sm text-gray-700 bg-gradient-to-r from-gray-100 to-gray-200 rounded-xl font-medium">
                        Page {currentPage} of {totalPages}
                      </span>
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="px-4 py-2 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                      >
                        Next
                        <ArrowRight size={16} className="inline ml-1" />
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Bulk Receive Modal */}
      {showBulkReceiveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl max-h-[95vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-red-500 to-blue-600 text-white p-4 flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button
                  onClick={handleReceiveOrder}
                  disabled={bulkReceiveLoading || !selectedPurchaseOrder}
                  className={`px-4 py-2 rounded-lg flex items-center gap-2 font-semibold transition-all duration-200 disabled:opacity-50 ${
                    selectedPurchaseOrder
                      ? 'bg-green-600 hover:bg-green-700 text-white' 
                      : 'bg-gray-500 text-white cursor-not-allowed'
                  }`}
                >
                  {bulkReceiveLoading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Receiving...
                    </>
                  ) : (
                    <>
                      <TruckIcon size={16} />
                      Receive Order
                    </>
                  )}
                </button>
                <div className="relative">
                  <select
                    value={selectedPurchaseOrder?.purchase_order_number || ''}
                    onChange={(e) => {
                      const selectedPO = e.target.value;
                      if (selectedPO) {
                        const order = approvedPurchases.find(p => p.purchase_order_number === selectedPO);
                        if (order) {
                          setSelectedPurchaseOrder(order);
                        }
                      } else {
                        setSelectedPurchaseOrder(null);
                      }
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-900 font-semibold rounded-lg font-mono text-sm border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select Order to Receive</option>
                    {approvedPurchases.map((purchase) => (
                      <option key={purchase.id} value={purchase.purchase_order_number}>
                        {purchase.purchase_order_number} - {purchase.supplier?.name || 'Unknown Supplier'}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-lg font-semibold text-white-700 mb-2">
                      Supplier's Invoice Number
                    </label>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <input
                      type="text"
                      value={supplierInvoiceNumber}
                      onChange={(e) => setSupplierInvoiceNumber(e.target.value)}
                      placeholder="Enter supplier invoice number"
                      className="w-full px-8 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

              </div>
              <div className="flex items-center space-x-4">
                <button
                  onClick={handleCloseBulkReceiveModal}
                  className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-colors"
                >
                  Close
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Company and Supplier Information - Side by Side */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                {/* Company's Information */}
                <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                      <Building2 size={16} className="text-blue-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Company's Information</h3>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Company (Store) Name
                        </label>
                        <input
                          type="text"
                          value="Grovalue Enterprise"
                          readOnly
                          className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-600 text-sm"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Purchase Date
                        </label>
                        <input
                          type="date"
                          value={selectedPurchaseOrder ? new Date(selectedPurchaseOrder.created_at).toISOString().slice(0, 10) : ''}
                          readOnly
                          className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-600 text-sm"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                        Expected Delivery Date
                        </label>
                        <input
                            type="date"
                            value={selectedPurchaseOrder?.expected_delivery_date || ''}
                            readOnly
                          className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-600 text-sm"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Purchase by / Agent
                        </label>
                        <input
                          type="text"
                          value={selectedPurchaseOrder?.agent || 'System User'}
                          readOnly
                          className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-600 text-sm"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Mode of Purchase
                        </label>
                        <input
                          type="text"
                          value={selectedPurchaseOrder?.mode_of_purchase || 'Not specified'}
                          readOnly
                          className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-600 text-sm"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Remark
                        </label>
                        <input
                            type="text"
                            value={selectedPurchaseOrder?.remark || 'No remarks'}
                            readOnly
                            className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-600 text-sm"
                          />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Supplier's Information */}
                <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                      <Building2 size={16} className="text-green-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Supplier's Information</h3>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Supplier's ID
                        </label>
                        <input
                          type="text"
                          value={selectedPurchaseOrder?.supplier?.supplierID || selectedPurchaseOrder?.supplierID || ''}
                          readOnly
                          className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-600 text-sm"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Email (optional)
                        </label>
                        <input
                          type="email"
                          value={selectedPurchaseOrder?.supplier_email || ''}
                          readOnly
                          className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-600 text-sm"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Supplier's Address
                        </label>
                        <input
                          type="text"
                          value={selectedPurchaseOrder?.supplier_address || ''}
                          readOnly
                          className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-600 text-sm"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Supplier's Name
                        </label>
                        <input
                          type="tel"
                          value={selectedPurchaseOrder?.supplier?.name || ''}
                          readOnly
                          className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-600 text-sm"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          value={selectedPurchaseOrder?.supplier_phone || ''}
                          readOnly
                          className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-600 text-sm"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Outstanding Amount
                        </label>
                        <input
                          type="text"
                          value={formatCurrency(Number(selectedPurchaseOrder?.supplier?.balance) || 0)}
                          readOnly
                          className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-600 text-sm"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

               {/* Input Fields Row */}
               <div className="flex items-center space-x-4 relative">
                     <div className="w-2/3 relative product-search-container">
                       <input
                         type="text"
                         value={searchQuery}
                         onChange={handleSearchChange}
                         onKeyDown={handleKeyDown}
                         onFocus={handleSearchFocus}
                         placeholder="SEARCH PRODUCTS BY NAME AND PRODUCTID..."
                         className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm placeholder-gray-500"
                       />
                      
                      {/* Product Dropdown */}
                      {products.length > 0 && showProductDropdown && (
                        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-50 max-h-60 overflow-y-auto">
                          <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 text-xs text-gray-600 font-medium">
                            {searchQuery.trim() ? `Found ${products.length} products` : `Showing all ${products.length} products`}
                          </div>
                          {products.map((product, index) => (
                            <div
                              key={product.id}
                              data-index={index}
                              className={`px-4 py-3 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors duration-150 ${
                                index === selectedIndex 
                                  ? (selectedIndex % 2 === 0 ? 'bg-red-100 border-red-300' : 'bg-blue-100 border-blue-300')
                                  : 'hover:bg-gray-50'
                              }`}
                              onClick={() => handleProductSelect(product)}
                            >
                              <div className="flex justify-between items-center">
                                <div className="flex-1">
                                  <div className="font-medium text-gray-900 text-sm leading-tight">
                                    {product.name}
                                  </div>
                                </div>
                                <div className="text-right">
                                  <div className="text-sm font-medium text-gray-900">
                                    {formatCurrency(parseFloat(product.cost_price || product.selling_price || product.price || 0))}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="w-20">
                      <input
                        id="purchase-quantity-input"
                        type="number"
                        defaultValue="1"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const uomSelect = document.getElementById('purchase-uom-select');
                            if (uomSelect) {
                              uomSelect.focus();
                            }
                          }
                        }}
                        className="w-full px-2 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg text-sm text-center"
                      />
                    </div>
                    <div className="w-20">
                      <select 
                        id="purchase-uom-select" 
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            e.stopPropagation();
                            addToCart();
                          }
                        }}
                        className="w-full px-2 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg text-sm"
                      >
                        <option value="ctn">Ctn</option>
                        <option value="pcs">Pcs</option>
                        <option value="kg">Kg</option>
                      </select>
                    </div>
                    <div className="w-24">
                      <input
                        id="purchase-cost-price-input"
                        type="number"
                        placeholder="0.00"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addToCart();
                          }
                        }}
                        className="w-full px-2 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg text-sm text-center"
                      />
                    </div>
                     <div className="w-24">
                       <input
                         id="purchase-current-qty-input"
                         type="number"
                         value={currentQuantity}
                         readOnly
                         className="w-full px-2 py-2 bg-gray-50 text-gray-600 border border-gray-300 rounded-lg text-sm text-center"
                       />
                     </div>
                   </div>

              {/* Purchase Items Table */}
              <div className="bg-white border border-gray-200 rounded-xl p-4">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center mr-3">
                    <ShoppingCart size={16} className="text-orange-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Purchase Items</h3>
                </div>
                
                {(!selectedPurchaseOrder || !selectedPurchaseOrder.items || selectedPurchaseOrder.items.length === 0) && additionalItems.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-gradient-to-r from-red-500 to-blue-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                      <Package size={32} className="text-white" />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">NEXAPOS</h4>
                    <p className="text-gray-600 mb-1">No items to receive</p>
                    <p className="text-sm text-gray-500">Select an order or add additional items above</p>
                  </div>
                ) : (
                  <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                    {/* Fixed Header */}
                    <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white">
                      <div className="grid grid-cols-12 gap-4 px-6 py-4 text-sm font-semibold uppercase tracking-wide">
                        <div className="col-span-3">Product</div>
                        <div className="col-span-1 text-center">Ordered</div>
                        <div className="col-span-1 text-center">Received</div>
                        <div className="col-span-1 text-center">UOM</div>
                        <div className="col-span-2 text-right">Price</div>
                        <div className="col-span-2 text-right">Amount</div>
                        <div className="col-span-1 text-center">Status</div>
                        <div className="col-span-1 text-center">Action</div>
                      </div>
                    </div>
                    
                    {/* Scrollable Body */}
                    <div className="max-h-64 overflow-y-auto bg-white">
                      <div className="divide-y divide-gray-100">
                        {/* Original Order Items */}
                        {selectedPurchaseOrder?.items?.filter(item => !removedItems.has(item.id)).map((item, index) => {
                          const receivedQty = receivedQuantities[item.id] || item.quantity;
                          const isPartialDelivery = receivedQty < item.quantity;
                          const isOverDelivery = receivedQty > item.quantity;
                          
                          return (
                            <div key={`original-${index}`} className="grid grid-cols-12 gap-4 items-center px-6 py-4 hover:bg-slate-50 transition-colors duration-150">
                              <div className="col-span-3">
                                <div className="text-sm font-medium text-gray-900 leading-tight">
                                  {item.product?.name || 'Unknown Product'}
                                </div>
                              </div>
                              <div className="col-span-1 text-center">
                                <span className="inline-flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
                                  {item.quantity}
                                </span>
                              </div>
                              <div className="col-span-1 text-center">
                                <input
                                  type="number"
                                  min="0"
                                  max={item.quantity * 2} // Allow up to 2x the ordered quantity
                                  value={receivedQty}
                                  onChange={(e) => handleQuantityChange(item.id, parseInt(e.target.value) || 0)}
                                  className="w-16 px-2 py-1 text-sm text-center border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                              </div>
                              <div className="col-span-1 text-center">
                                <span className="text-sm text-gray-600 font-medium">
                                  {item.product?.unit_of_measure || 'pcs'}
                                </span>
                              </div>
                              <div className="col-span-2 text-right">
                                <div className="text-sm font-medium text-gray-900">
                                  {formatCurrency(Number(item.cost_price) || 0)}
                                </div>
                              </div>
                              <div className="col-span-2 text-right">
                                <div className="text-sm font-bold text-gray-900">
                                  {formatCurrency(receivedQty * (Number(item.cost_price) || 0))}
                                </div>
                              </div>
                              <div className="col-span-1 text-center">
                                {isPartialDelivery ? (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                    <Clock size={12} className="mr-1" />
                                    Partial
                                  </span>
                                ) : isOverDelivery ? (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                    <Plus size={12} className="mr-1" />
                                    Extra
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    <CheckCircle size={12} className="mr-1" />
                                    Complete
                                  </span>
                                )}
                              </div>
                              <div className="col-span-1 text-center">
                                <button
                                  onClick={() => handleRemoveItem(item.id)}
                                  className="inline-flex items-center justify-center w-8 h-8 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 hover:text-red-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
                                  title="Remove item (supplier couldn't supply)"
                                >
                                  <Minus size={14} />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                        
                        {/* Additional Items */}
                        {additionalItems.map((item, index) => (
                          <div key={`additional-${index}`} className="grid grid-cols-12 gap-4 items-center px-6 py-4 hover:bg-slate-50 transition-colors duration-150 bg-purple-50">
                            <div className="col-span-3">
                              <div className="text-sm font-medium text-gray-900 leading-tight">
                                {item.product_name}
                              </div>
                            </div>
                            <div className="col-span-1 text-center">
                              <span className="inline-flex items-center justify-center w-8 h-8 bg-purple-100 text-purple-800 text-sm font-medium rounded-full">
                                -
                              </span>
                            </div>
                            <div className="col-span-1 text-center">
                              <span className="inline-flex items-center justify-center w-8 h-8 bg-purple-100 text-purple-800 text-sm font-medium rounded-full">
                                {item.quantity}
                              </span>
                            </div>
                            <div className="col-span-1 text-center">
                              <span className="text-sm text-gray-600 font-medium">
                                {item.uom}
                              </span>
                            </div>
                            <div className="col-span-2 text-right">
                              <div className="text-sm font-medium text-gray-900">
                                {formatCurrency(item.cost_price)}
                              </div>
                            </div>
                            <div className="col-span-2 text-right">
                              <div className="text-sm font-bold text-gray-900">
                                {formatCurrency(item.subtotal)}
                              </div>
                            </div>
                            <div className="col-span-2 text-center">
                              <div className="flex items-center justify-center space-x-2">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                  <Plus size={12} className="mr-1" />
                                  Additional
                                </span>
                                <button
                                  onClick={() => setAdditionalItems(additionalItems.filter((_, i) => i !== index))}
                                  className="text-red-600 hover:text-red-800 p-1"
                                >
                                  <X size={12} />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

            </div>

            {/* Modal Footer */}
            <div className="bg-gradient-to-r from-red-500 to-blue-600 text-white p-4">
              <div className="flex items-center justify-between space-x-8">
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-white/80">Items:</span>
                  <span className="text-lg font-bold text-white">
                    {(selectedPurchaseOrder?.items?.filter(item => !removedItems.has(item.id)).length || 0) + additionalItems.length}
                  </span>
                  {removedItems.size > 0 && (
                    <span className="text-xs text-red-200 bg-red-500/20 px-2 py-1 rounded-full">
                      {removedItems.size} removed
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-white/80">Received Quantity:</span>
                  <span className="text-lg font-bold text-white">
                    {(() => {
                      const originalItemsQty = selectedPurchaseOrder?.items?.reduce((sum, item) => {
                        // Skip removed items
                        if (removedItems.has(item.id)) return sum;
                        const receivedQty = receivedQuantities[item.id] || item.quantity;
                        return sum + (receivedQty || 0);
                      }, 0) || 0;
                      
                      const additionalItemsQty = additionalItems.reduce((sum, item) => {
                        return sum + (item.quantity || 0);
                      }, 0);
                      
                      return originalItemsQty + additionalItemsQty;
                    })()}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-white/80">Received Amount:</span>
                  <span className="text-lg font-bold text-white">
                    {formatCurrency((() => {
                      const originalItemsTotal = selectedPurchaseOrder?.items?.reduce((sum, item) => {
                        // Skip removed items
                        if (removedItems.has(item.id)) return sum;
                        const receivedQty = receivedQuantities[item.id] || item.quantity;
                        return sum + (receivedQty * (Number(item.cost_price) || 0));
                      }, 0) || 0;
                      
                      const additionalItemsTotal = additionalItems.reduce((sum, item) => {
                        return sum + (item.quantity * item.cost_price);
                      }, 0);
                      
                      return originalItemsTotal + additionalItemsTotal;
                    })())}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-lg font-bold text-white">Total Amount:</span>
                  <span className="text-2xl font-bold text-yellow-300">
                    {formatCurrency((() => {
                      const originalItemsTotal = selectedPurchaseOrder?.items?.reduce((sum, item) => {
                        // Skip removed items
                        if (removedItems.has(item.id)) return sum;
                        const receivedQty = receivedQuantities[item.id] || item.quantity;
                        return sum + (receivedQty * (Number(item.cost_price) || 0));
                      }, 0) || 0;
                      
                      const additionalItemsTotal = additionalItems.reduce((sum, item) => {
                        return sum + (item.quantity * item.cost_price);
                      }, 0);
                      
                      return originalItemsTotal + additionalItemsTotal;
                    })())}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Receipt Modal */}
      {showReceiptModal && receiptData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[95vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-4 flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <Receipt size={20} />
                </div>
                <div>
                  <h2 className="text-xl font-bold">Order Received</h2>
                  <p className="text-green-100 text-sm">Purchase Order: {receiptData.purchase_order_number}</p>
                </div>
                <button
                  onClick={handleCloseReceiptModal}
                  className="p-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors"
                >
                  <CloseIcon size={16} />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div id="receipt-content" className="receipt-content">
                {/* Company Header */}
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-gradient-to-r from-red-500 to-blue-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <Receipt size={32} className="text-white" />
                  </div>
                  <h1 className="text-2xl font-bold text-gray-900 mb-2">{companySettings.company_name}</h1>
                  <p className="text-gray-600">Purchase Order Receipt</p>
                </div>

                {/* Order Details */}
                <div className="grid grid-cols-2 gap-6 mb-6">
                  {/* Company Information */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Company Information</h3>
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Company:</span>
                        <span className="text-gray-900 font-medium">{companySettings.company_name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Order Date:</span>
                        <span className="text-gray-900 font-medium">{new Date(receiptData.created_at).toLocaleDateString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Received Date:</span>
                        <span className="text-gray-900 font-medium">{new Date().toLocaleDateString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Agent:</span>
                        <span className="text-gray-900 font-medium">System User</span>
                      </div>
                    </div>
                  </div>

                  {/* Supplier Information */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Supplier Information</h3>
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Supplier:</span>
                        <span className="text-gray-900 font-medium">{receiptData.supplier?.name || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Supplier ID:</span>
                        <span className="text-gray-900 font-medium">{receiptData.supplier?.supplierID || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Phone:</span>
                        <span className="text-gray-900 font-medium">{receiptData.supplier?.phone || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Email:</span>
                        <span className="text-gray-900 font-medium">{receiptData.supplier?.email || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Order Items */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Items</h3>
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                      <div className="grid grid-cols-6 gap-4 text-sm font-semibold text-gray-700 uppercase tracking-wide">
                        <div>Product</div>
                        <div className="text-center">Quantity</div>
                        <div className="text-center">UOM</div>
                        <div className="text-right">Price</div>
                        <div className="text-right">Amount</div>
                        <div className="text-center">Status</div>
                      </div>
                    </div>
                    <div className="divide-y divide-gray-200">
                      {receiptData.items?.map((item, index) => (
                        <div key={index} className="px-4 py-3">
                          <div className="grid grid-cols-6 gap-4 items-center text-sm">
                            <div className="font-medium text-gray-900">
                              {item.product?.name || 'Unknown Product'}
                            </div>
                            <div className="text-center">
                              <span className="inline-flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
                                {item.quantity}
                              </span>
                            </div>
                            <div className="text-center text-gray-600">
                              {item.product?.unit_of_measure || 'pcs'}
                            </div>
                            <div className="text-right font-medium text-gray-900">
                              {formatCurrency(Number(item.cost_price) || 0)}
                            </div>
                            <div className="text-right font-bold text-gray-900">
                              {formatCurrency(Number(item.subtotal) || 0)}
                            </div>
                            <div className="text-center">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                <CheckCircle size={12} className="mr-1" />
                                Received
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Order Summary */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold text-gray-900">Total Amount:</span>
                    <span className="text-2xl font-bold text-gray-900">
                      {formatCurrency(Number(receiptData.total_cost) || 0)}
                    </span>
                  </div>
                </div>

                {/* Order Status */}
                <div className="mt-6 text-center">
                  <div className="inline-flex items-center px-4 py-2 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                    <CheckCircle size={16} className="mr-2" />
                    Order Successfully Received
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 p-4 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Receipt generated on {new Date().toLocaleString()}
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={handlePrintReceipt}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 font-semibold transition-all duration-200"
                >
                  <Printer size={16} />
                  Print
                </button>
                <button
                  onClick={generatePDF}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-2 font-semibold transition-all duration-200"
                >
                  <FileText size={16} />
                  Generate PDF
                </button>
                <button
                  onClick={handleSendWhatsApp}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center gap-2 font-semibold transition-all duration-200"
                >
                  <CheckCircle size={16} />
                  Send WhatsApp
                </button>
                <button
                  onClick={handleCloseReceiptModal}
                  className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg flex items-center gap-2 font-semibold transition-all duration-200"
                >
                  <CloseIcon size={16} />
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && viewData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-4 flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <Eye size={20} />
                </div>
                <div>
                  <h2 className="text-xl font-bold">Purchase Order Details</h2>
                  <p className="text-blue-100 text-sm">PO: {viewData.purchase_order_number}</p>
                </div>
              </div>
              <button
                onClick={handleCloseViewModal}
                className="p-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors"
              >
                <CloseIcon size={16} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Company Header */}
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-gradient-to-r from-red-500 to-blue-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Receipt size={32} className="text-white" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">{companySettings.company_name}</h1>
                <p className="text-gray-600">Purchase Order Details</p>
              </div>

              {/* Order Details */}
              <div className="grid grid-cols-2 gap-6 mb-6">
                {/* Company Information */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Company Information</h3>
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Company:</span>
                      <span className="text-gray-900 font-medium">NEXAPOS</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Order Date:</span>
                      <span className="text-gray-900 font-medium">{new Date(viewData.created_at).toLocaleDateString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Received Date:</span>
                      <span className="text-gray-900 font-medium">{new Date(viewData.updated_at).toLocaleDateString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Agent:</span>
                      <span className="text-gray-900 font-medium">System User</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Status:</span>
                      <span className="text-gray-900 font-medium capitalize">{viewData.status}</span>
                    </div>
                  </div>
                </div>

                {/* Supplier Information */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Supplier Information</h3>
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Supplier:</span>
                      <span className="text-gray-900 font-medium">{viewData.supplier?.name || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Supplier ID:</span>
                      <span className="text-gray-900 font-medium">{viewData.supplier?.supplierID || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Phone:</span>
                      <span className="text-gray-900 font-medium">{viewData.supplier?.phone || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Email:</span>
                      <span className="text-gray-900 font-medium">{viewData.supplier?.email || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Address:</span>
                      <span className="text-gray-900 font-medium">{viewData.supplier?.address || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Order Items */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Items</h3>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                    <div className="grid grid-cols-6 gap-4 text-sm font-semibold text-gray-700 uppercase tracking-wide">
                      <div>Product</div>
                      <div className="text-center">Quantity</div>
                      <div className="text-center">UOM</div>
                      <div className="text-right">Price</div>
                      <div className="text-right">Amount</div>
                      <div className="text-center">Status</div>
                    </div>
                  </div>
                  <div className="divide-y divide-gray-200">
                    {viewData.items?.map((item, index) => (
                      <div key={index} className="px-4 py-3">
                        <div className="grid grid-cols-6 gap-4 items-center text-sm">
                          <div className="font-medium text-gray-900">
                            {item.product?.name || 'Unknown Product'}
                          </div>
                          <div className="text-center">
                            <span className="inline-flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
                              {item.quantity}
                            </span>
                          </div>
                          <div className="text-center text-gray-600">
                            {item.product?.unit_of_measure || 'pcs'}
                          </div>
                          <div className="text-right font-medium text-gray-900">
                            Ghs {(Number(item.cost_price) || 0).toFixed(2)}
                          </div>
                          <div className="text-right font-bold text-gray-900">
                            Ghs {(Number(item.subtotal) || 0).toFixed(2)}
                          </div>
                          <div className="text-center">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <CheckCircle size={12} className="mr-1" />
                              Received
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Order Summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold text-gray-900">Total Amount:</span>
                  <span className="text-2xl font-bold text-gray-900">
                    {formatCurrency(Number(viewData.total_cost) || 0)}
                  </span>
                </div>
              </div>

              {/* Order Status */}
              <div className="mt-6 text-center">
                <div className="inline-flex items-center px-4 py-2 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                  <CheckCircle size={16} className="mr-2" />
                  Order Successfully Received
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 p-4 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Order viewed on {new Date().toLocaleString()}
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => {
                    handleCloseViewModal();
                    handleOpenReceiptModal(viewData);
                  }}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 font-semibold transition-all duration-200"
                >
                  <Printer size={16} />
                  Print Receipt
                </button>
                <button
                  onClick={handleCloseViewModal}
                  className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg flex items-center gap-2 font-semibold transition-all duration-200"
                >
                  <CloseIcon size={16} />
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default ReceivePurchases;
